import jsYaml from 'js-yaml';
import { ArchitectureNode, ArchitectureEdge } from '../types';

// Enhanced YAML structure with metadata
interface ComponentDefinition {
  type: string;
  specs: any;
  locked?: boolean;
  isCluster?: boolean;
  connections: Array<{
    target: string;
    type: string;
    port?: string;
    sourceHandle?: string;
    targetHandle?: string;
    edgeType?: string;
  }>;
  nestedComponents?: Record<string, ComponentDefinition>;
}

interface EnhancedYamlOutput {
  metadata: {
    version: string;
    tool: string;
    toolVersion: string;
    created: string;
    description?: string;
  };
  architecture: {
    name: string;
    components: Record<string, ComponentDefinition>;
  };
}

// Helper function to process nodes and edges recursively
const processComponents = (nodes: ArchitectureNode[], edges: ArchitectureEdge[]): Record<string, ComponentDefinition> => {
  const components: Record<string, ComponentDefinition> = {};
  
  nodes.forEach(node => {
    // Find all outgoing edges from this node
    const connections = edges
      .filter(edge => edge.source === node.id)
      .map(edge => {
        const targetNode = nodes.find(n => n.id === edge.target);
        const targetName = targetNode ? (targetNode.data.label || targetNode.id) : edge.target;
        
        const connection: any = {
          target: targetName,
          type: edge.data?.type || 'Unknown',
        };
        
        // Only add optional fields if they have values
        if (edge.data?.port) connection.port = edge.data.port;
        if (edge.sourceHandle) connection.sourceHandle = edge.sourceHandle;
        if (edge.targetHandle) connection.targetHandle = edge.targetHandle;
        if (edge.type && edge.type !== 'default') connection.edgeType = edge.type;
        
        return connection;
      });

    const componentName = node.data.label || node.id;
    const component: ComponentDefinition = {
      type: node.data.type,
      specs: node.data.specs || {},
      connections: connections
    };
    
    // Only add optional fields if they have values
    if (node.data.locked) component.locked = true;
    if (node.data.isCluster) component.isCluster = true;
    
    // Recursively process nested architecture
    if (node.data.nestedArchitecture) {
      const nestedComponents = processComponents(
        node.data.nestedArchitecture.nodes,
        node.data.nestedArchitecture.edges
      );
      if (Object.keys(nestedComponents).length > 0) {
        component.nestedComponents = nestedComponents;
      }
    }
    
    components[componentName] = component;
  });
  
  return components;
};

export const generateYaml = (nodes: ArchitectureNode[], edges: ArchitectureEdge[]): string => {
  const output: EnhancedYamlOutput = {
    metadata: {
      version: '1.0',
      tool: 'ArchWeaver',
      toolVersion: '1.0.0',
      created: new Date().toISOString(),
      description: 'Architecture diagram exported from ArchWeaver'
    },
    architecture: {
      name: 'Architecture Diagram',
      components: processComponents(nodes, edges)
    }
  };

  return jsYaml.dump(output, { indent: 2, lineWidth: -1 });
};
